import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import useAccountMenu from 'hooks/useAccountMenu';
import useHasApp from 'hooks/useHasApp';
import { LogOut, Magnet, Monitor, X } from 'lucide-react';
import { heroProspects, presentations } from 'routes/routes';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

type MobileNavItem = Readonly<{
  label: string;
  to: string;
  icon: React.ElementType;
}>;

/**
 * Account destinations available on phones. The company/admin pages
 * (Company Settings, Company Accounts, Plans, Organizations, Performance
 * Dashboard) aren't mobile-ready, so the phone account sheet shows only these
 * plus Sign out. The desktop sidebar dropdown still lists everything.
 */
const MOBILE_ACCOUNT_ITEM_KEYS = ['profile'];

/**
 * Bottom toolbar shown only on phones (md:hidden). On mobile the full sidebar is
 * intentionally unavailable — navigation is narrowed to Presentations and Leads so
 * customers can create and send presentations directly from their phones, plus an
 * account avatar that opens a full-screen account sheet. Presentations is gated via
 * `useHasApp`, mirroring AppSidebar and the RequireApp route guard — for unentitled
 * companies the tab would only dead-end at the SSO failure screen.
 */
const MobileBottomNav = (): JSX.Element => {
  const { t } = useTranslation('common');
  const { items, signOut, displayName, companyName, initials } = useAccountMenu();
  const [accountOpen, setAccountOpen] = useState(false);

  const accountItems = items.filter((item) => MOBILE_ACCOUNT_ITEM_KEYS.includes(item.key));
  const { hasApp: hasPresentations } = useHasApp()('presentations');

  const navItems: MobileNavItem[] = [
    ...(hasPresentations ? [{ label: t('nav-presentations', 'Presentations'), to: presentations, icon: Monitor }] : []),
    { label: t('nav-prospects', 'Leads'), to: heroProspects, icon: Magnet },
  ];

  const tabClass = 'flex flex-col items-center justify-center gap-1 border-t-2 px-4 py-3';
  const sheetItemClass =
    'flex w-full items-center gap-3 rounded-lg px-3 py-3.5 text-left text-base font-medium text-card-foreground transition-colors hover:bg-muted';

  const runAndClose = (action: () => void) => {
    setAccountOpen(false);
    action();
  };

  return (
    <nav
      aria-label={t('nav-mobile-primary-label', 'Primary')}
      className="flex shrink-0 items-center justify-center gap-4 border-t border-border bg-card px-6 pb-[env(safe-area-inset-bottom)] text-card-foreground md:hidden"
    >
      {navItems.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          className={({ isActive }) => cn(tabClass, isActive ? 'border-primary' : 'border-transparent')}
        >
          {({ isActive }) => (
            <>
              <item.icon className={cn('size-6', isActive ? 'text-primary' : 'text-card-foreground')} />
              <span className="text-xs font-medium text-card-foreground">{item.label}</span>
            </>
          )}
        </NavLink>
      ))}

      <Sheet open={accountOpen} onOpenChange={setAccountOpen}>
        <SheetTrigger asChild>
          <button
            type="button"
            aria-label={t('nav-account', 'Account')}
            className={cn(tabClass, 'border-transparent data-[state=open]:border-primary')}
          >
            <Avatar className="size-10">
              <AvatarFallback className="text-xs font-semibold">{initials}</AvatarFallback>
            </Avatar>
          </button>
        </SheetTrigger>

        <SheetContent
          side="left"
          showCloseButton={false}
          className="h-[100dvh] w-screen max-w-none gap-0 bg-card p-0 pt-[calc(env(safe-area-inset-top)+1.5rem)] sm:max-w-none"
        >
          <SheetHeader className="flex-row items-center gap-3 border-b p-4">
            <Avatar className="size-12">
              <AvatarFallback className="text-base font-semibold">{initials}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1 text-left">
              <SheetTitle className="truncate text-base">{displayName}</SheetTitle>
              <SheetDescription className="truncate">{companyName}</SheetDescription>
            </div>
            <SheetClose
              className="flex size-9 shrink-0 items-center justify-center self-start rounded-md text-muted-foreground transition-colors hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={t('close', 'Close')}
            >
              <X className="size-5" />
            </SheetClose>
          </SheetHeader>

          <div className="flex flex-col gap-1 p-2">
            {accountItems.map((item) => (
              <button key={item.key} type="button" onClick={() => runAndClose(item.onClick)} className={sheetItemClass}>
                <item.icon className="size-5 text-muted-foreground" />
                {item.label}
              </button>
            ))}
          </div>

          <div className="mt-auto border-t p-2 pt-3 pb-[calc(env(safe-area-inset-bottom)+1.75rem)]">
            <button type="button" onClick={() => runAndClose(signOut)} className={sheetItemClass}>
              <LogOut className="size-5 text-muted-foreground" />
              {t('nav-sign-out', 'Log out')}
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </nav>
  );
};

export default MobileBottomNav;
